import type { Locale } from "@saas/internationalization";
import i18next from "i18next";
import { initReactI18next } from "react-i18next";

export const errorCatalogs = {
  en: {
    AUTHENTICATION_REQUIRED: "Please sign in to continue.",
    AUTHORIZATION_DENIED: "You do not have permission to perform this action.",
    VALIDATION_FAILED: "Check the information and try again.",
    RESOURCE_NOT_FOUND: "The requested resource was not found.",
    TENANT_ACCESS_DENIED: "You do not have access to this organization.",
    PLAN_LIMIT_REACHED: "Your current plan limit has been reached.",
    ENTITLEMENT_REQUIRED: "Your plan does not include this capability.",
    FEATURE_DISABLED: "This feature is not currently available.",
    RATE_LIMITED: "Too many attempts. Please wait and try again.",
    CONFLICT: "This change conflicts with the current state.",
    INTERNAL_ERROR: "Something went wrong. Please try again.",
  },
  "pt-BR": {
    AUTHENTICATION_REQUIRED: "Entre para continuar.",
    AUTHORIZATION_DENIED: "Você não tem permissão para realizar esta ação.",
    VALIDATION_FAILED: "Revise as informações e tente novamente.",
    RESOURCE_NOT_FOUND: "O recurso solicitado não foi encontrado.",
    TENANT_ACCESS_DENIED: "Você não tem acesso a esta organização.",
    PLAN_LIMIT_REACHED: "O limite do seu plano foi atingido.",
    ENTITLEMENT_REQUIRED: "Seu plano não inclui este recurso.",
    FEATURE_DISABLED: "Este recurso não está disponível no momento.",
    RATE_LIMITED: "Muitas tentativas. Aguarde e tente novamente.",
    CONFLICT: "Esta alteração conflita com o estado atual.",
    INTERNAL_ERROR: "Algo deu errado. Tente novamente.",
  },
  "pt-PT": {
    AUTHENTICATION_REQUIRED: "Inicie sessão para continuar.",
    AUTHORIZATION_DENIED: "Não tem permissão para realizar esta ação.",
    VALIDATION_FAILED: "Verifique as informações e tente novamente.",
    RESOURCE_NOT_FOUND: "O recurso solicitado não foi encontrado.",
    TENANT_ACCESS_DENIED: "Não tem acesso a esta organização.",
    PLAN_LIMIT_REACHED: "O limite do seu plano foi atingido.",
    ENTITLEMENT_REQUIRED: "O seu plano não inclui esta funcionalidade.",
    FEATURE_DISABLED: "Esta funcionalidade não está disponível de momento.",
    RATE_LIMITED: "Demasiadas tentativas. Aguarde e tente novamente.",
    CONFLICT: "Esta alteração entra em conflito com o estado atual.",
    INTERNAL_ERROR: "Ocorreu um erro. Tente novamente.",
  },
  es: {
    AUTHENTICATION_REQUIRED: "Ingresa para continuar.",
    AUTHORIZATION_DENIED: "No tienes permiso para realizar esta acción.",
    VALIDATION_FAILED: "Revisa la información e inténtalo de nuevo.",
    RESOURCE_NOT_FOUND: "No se encontró el recurso solicitado.",
    TENANT_ACCESS_DENIED: "No tienes acceso a esta organización.",
    PLAN_LIMIT_REACHED: "Alcanzaste el límite de tu plan.",
    ENTITLEMENT_REQUIRED: "Tu plan no incluye esta capacidad.",
    FEATURE_DISABLED: "Esta función no está disponible ahora.",
    RATE_LIMITED: "Demasiados intentos. Espera e inténtalo de nuevo.",
    CONFLICT: "Este cambio entra en conflicto con el estado actual.",
    INTERNAL_ERROR: "Algo salió mal. Inténtalo de nuevo.",
  },
} satisfies Record<Locale, Record<string, string>>;

const development = import.meta.env.MODE !== "production";
void i18next.use(initReactI18next).init({
  resources: Object.fromEntries(
    Object.entries(errorCatalogs).map(([locale, errors]) => [
      locale,
      { errors },
    ]),
  ),
  lng: "en",
  fallbackLng: "en",
  defaultNS: "errors",
  interpolation: { escapeValue: false },
  returnNull: false,
  saveMissing: development,
});
if (development) {
  i18next.on("missingKey", (_languages, _namespace, key) =>
    console.warn(`Missing translation: ${key}`),
  );
}

export async function activateLocale(locale: Locale): Promise<void> {
  if (i18next.language !== locale) await i18next.changeLanguage(locale);
}
export function translateApiError(
  locale: Locale,
  code: string | undefined,
): string {
  const safeCode = code && code in errorCatalogs.en ? code : "INTERNAL_ERROR";
  return i18next.getFixedT(locale, "errors")(safeCode);
}
export { i18next };
